import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getQuotations,
  getQuotation,
  createQuotation,
  editQuotation,
  deleteQuotation,
  getAllQuotations,
  getTrashedQuotations,
  restoreQuotation,
  forceDeleteQuotation,
  searchQuotations,
} from "api/quotationApi"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const QUOTATION_QUERY_KEY = "quotations"

// -------------------- FETCH PAGINATED --------------------
export const useQuotations = (params = {}, enabled = true) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    getQuotations,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useQuotation = (id, enabled = true) => {
  return useResourceItem(
    QUOTATION_QUERY_KEY,
    getQuotation,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateQuotation = () => {
  return useCreateResource(createQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditQuotation = () => {
  return useEditResource(editQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteQuotation = () => {
  return useDeleteResource(deleteQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- FORCE DELETE --------------------
export const useForceDeleteQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteQuotation,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// Get all quotations without pagination
export const useAllQuotations = (params = {}) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    getAllQuotations,
    { ...params, all: true },
    true
  )
}

// Get trashed quotations
export const useTrashedQuotations = (params = {}) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    getTrashedQuotations,
    params,
    true
  )
}

// Restore a trashed quotation
export const useRestoreQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreQuotation,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([QUOTATION_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([QUOTATION_QUERY_KEY, id], restoredItem)
    },
  })
}

// -------------------- SEARCH --------------------
export const useSearchQuotations = (searchTerm, params = {}, enabled = true) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    searchQuotations,
    { ...params, search: searchTerm },
    enabled
  )
}
